import React, { useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { Modal } from '../../ui/Modal';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Badge';
import { practiceStatusVariant } from '../practice/PracticeDetailModal';
import { practicesStore } from '../../../services/practicesStore';
import { attendeeCount, requestedCourseLabel, requesterName } from '../../../utils/practices';
import { Practice } from '../../../types/practice';
import { TrainingCourse } from '../../../types/trainingCourse';
interface AssignPracticesModalProps {
  isOpen: boolean;
  course: TrainingCourse;
  practices: Practice[];
  onClose: () => void;
}

// Check-list of the practices to assign to the course (negative ones excluded)
export function AssignPracticesModal({
  isOpen,
  course,
  practices,
  onClose
}: AssignPracticesModalProps) {
  const candidates = useMemo(() => practices.filter(p => p.status !== 'Negativo')
  // Same product first
  .sort((a, b) => Number(b.productCode === course.productCode) - Number(a.productCode === course.productCode)), [practices, course.productCode]);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState('');
  useEffect(() => {
    if (!isOpen) return;
    setQuery('');
    setChecked(new Set(candidates.filter(p => p.courseCode === course.courseCode).map(p => p.id)));
  }, [isOpen, candidates, course.courseCode]);
  const q = query.trim().toLowerCase();
  const visible = candidates.filter(p => !q || requesterName(p).toLowerCase().includes(q) || requestedCourseLabel(p).toLowerCase().includes(q) || p.productCode.toLowerCase().includes(q));
  const toggle = (id: string) => setChecked(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const confirm = () => {
    const onDemand = course.status === 'ondemand';
    candidates.forEach(p => {
      const isLinked = p.courseCode === course.courseCode;
      if (checked.has(p.id) && !isLinked) {
        practicesStore.update(p.id, {
          courseCode: course.courseCode,
          schedulingConfirmed: false,
          ...(onDemand ? {
            deliveryMode: 'elearning',
            courseStatus: 'On-demand'
          } : p.deliveryMode === 'elearning' ? {
            deliveryMode: undefined,
            courseStatus: 'Programmato'
          } : {})
        });
      }
      if (!checked.has(p.id) && isLinked) {
        practicesStore.update(p.id, {
          courseCode: undefined,
          schedulingConfirmed: false
        });
      }
    });
    onClose();
  };
  const total = candidates.filter(p => checked.has(p.id)).reduce((sum, p) => sum + (attendeeCount(p) || 0), 0);
  return <Modal isOpen={isOpen} onClose={onClose} title="Assegna pratiche al corso" size="xl" data-id="element-1094" data-mp="z2q8rs44f635">
      <div className="space-y-4" data-id="element-1095" data-mp="2jn3o6mhd3li0">
        <p className="text-sm text-slate-500 -mt-1" data-id="element-1096" data-mp="2jjzdr7daztiw">
          Seleziona le pratiche da assegnare a{' '}
          <strong className="text-slate-700" data-id="element-1097" data-mp="2fidwl9tqkt2w">
            {course.courseCode} · {course.name || 'Corso senza nome'}
          </strong>
          . I loro partecipanti diventano gli utenti del corso.
        </p>
        <div className="relative" data-id="element-1098" data-mp="2ojc2jxnqjao4">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-1099" data-mp="3399soj586afy" />
          <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Cerca richiedente o corso richiesto..." aria-label="Cerca pratiche" className="w-full rounded-xl border-2 border-slate-200 bg-slate-50/50 pl-9 pr-4 py-2 text-sm focus:border-seform-orange focus:bg-white focus:outline-none" data-id="element-1100" data-mp="2ybri6obk7n5u" />
        </div>
        <ul className="rounded-xl border border-slate-200 divide-y divide-slate-100 max-h-96 overflow-y-auto" data-id="element-1101" data-mp="1cyx26j64sn26">
          {visible.map(p => {
          const otherCourse = p.courseCode && p.courseCode !== course.courseCode ? p.courseCode : null;
          return <li key={p.id} data-id="element-1102" data-mp="1oe068nvkkkei">
                <label className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-slate-50" data-id="element-1103" data-mp="1odrooioi4qwz">
                  <input type="checkbox" className="rounded border-slate-300 text-seform-orange focus:ring-seform-orange" checked={checked.has(p.id)} onChange={() => toggle(p.id)} data-id="element-1104" data-mp="1t9yy4lg3wkjo" />
                  <span className="flex-1 min-w-0" data-id="element-1105" data-mp="15kw9gtn21rfo">
                    <span className="block text-sm font-medium text-slate-800 truncate" data-id="element-1106" data-mp="1acxj07su9420">
                      {requesterName(p)}
                    </span>
                    <span className="block text-xs text-slate-500 truncate" data-id="element-1107" data-mp="cxjmwjgw9jmd">
                      {requestedCourseLabel(p)} · {attendeeCount(p) ?? 0} partecipanti
                      {otherCourse && ` · ora assegnata a ${otherCourse}`}
                    </span>
                  </span>
                  <Badge variant={practiceStatusVariant[p.status]} size="sm" data-id="element-1108" data-mp="5etti1eh9z8p">
                    {p.status}
                  </Badge>
                </label>
              </li>;
        })}
          {visible.length === 0 && <li className="px-4 py-6 text-center text-sm text-slate-500" data-id="element-1109" data-mp="3njx39zfe2zfg">
              Nessuna pratica trovata.
            </li>}
        </ul>
        <div className="flex items-center justify-between gap-3 pt-2" data-id="element-1110" data-mp="3irm3qxok9fnf">
          <span className="text-sm text-slate-500" data-id="element-1111" data-mp="3dtav2qa05b6s">
            {checked.size} pratiche · {total} partecipanti
          </span>
          <div className="flex gap-3" data-id="element-1112" data-mp="38wke80qdibkb">
            <Button variant="flat" color="default" onClick={onClose} data-id="element-1113" data-mp="33vwnzh9yxyr5">
              Annulla
            </Button>
            <Button onClick={confirm} data-id="element-1114" data-mp="2zuktaxc8mnc3">Conferma assegnazione</Button>
          </div>
        </div>
      </div>
    </Modal>;
}